import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import type { Node, Space, View, ViewFilters } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { spaceBySlug, viewsOf } from "../lib/select";
import { send } from "../lib/actions";
import { navigate, spaceHref } from "../lib/nav";
import { useFilters, readFilters, toggleIn, writeFilters } from "../lib/filters";
import { useShortcut } from "../lib/keys";
import { can, isContractorIn, ROLE_LABEL } from "../lib/access";
import { Icon, VIEW_ICON } from "../components/icons";
import { Avatar, Dialog, Empty } from "../components/primitives";
import { MenuButton } from "../components/pickers";
import { NewItemDialog, NewViewDialog, ProjectSettingsDialog } from "../components/dialogs";
import { TopBar } from "../components/Shell";
import { TeamDialog } from "../components/TeamDialog";
import { PlanUpdatesDialog, ShipDialog } from "../components/Cycle";
import { FilterChips, ViewTools } from "../components/FilterBar";
import { ContractorWork } from "../views/ContractorWork";
import { Board } from "../views/Board";
import { List } from "../views/List";
import { Overview } from "./Overview";
import { groupOptions, newItemParent, viewKinds } from "../views/common";

const Table = lazy(() => import("../views/Table").then((m) => ({ default: m.Table })));
const Gallery = lazy(() => import("../views/Gallery").then((m) => ({ default: m.Gallery })));
const Timeline = lazy(() => import("../views/Timeline").then((m) => ({ default: m.Timeline })));
const Calendar = lazy(() => import("../views/Calendar").then((m) => ({ default: m.Calendar })));
const Refs = lazy(() => import("../views/Refs").then((m) => ({ default: m.Refs })));

type DialogKind = null | "item" | "milestone" | "view" | "settings" | "team" | "plan";

export function SpacePage({ slug, viewId }: { slug: string; viewId?: string }) {
  const t = useTables();
  const space = spaceBySlug(t, slug);
  const views = space ? viewsOf(t, space.id) : [];
  const view = viewId ? views.find((v) => v.id === viewId) : undefined;
  useEffect(() => {
    if (space) document.title = `${view?.name ?? "Overview"} · ${space.name} · melo.build`;
  }, [space, view]);
  if (!space) return <Missing />;
  if (isContractorIn(space.id))
    return (
      <>
        <TopBar title={space.name} />
        <div className="content">
          <ContractorWork space={space} />
        </div>
      </>
    );
  if (viewId && !view) return <Missing />;
  return <SpaceBody space={space} view={view} views={views} />;
}

function Missing() {
  return (
    <>
      <TopBar title="Not found" />
      <Empty title="This project or view doesn't exist, or you can't see it.">
        <Link href="/" className="btn">
          Go home
        </Link>
      </Empty>
    </>
  );
}

function SpaceBody({ space, view, views }: { space: Space; view: View | undefined; views: View[] }) {
  const t = useTables();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [shipping, setShipping] = useState<Node | null>(null);
  const producer = can(space.id, "producer");
  const member = can(space.id, "member");
  const search = useSearch();
  const [filters, setFilters, overridden] = useFilters(view?.config.filters ?? {});
  const mine = (filters.assignees ?? []).includes("me");
  const toggleMine = () => {
    const cur = readFilters(search) ?? view?.config.filters ?? {};
    writeFilters(toggleIn(cur, "assignees", "me"));
  };
  useShortcut("q", toggleMine, Boolean(view && view.type !== "refs"));
  useShortcut("c", () => setDialog("item"), member);
  const kinds = view ? viewKinds(t, space, view) : [];

  return (
    <>
      <TopBar
        crumbs={view ? [{ label: space.name, href: spaceHref(space.slug) }] : []}
        title={view?.name ?? space.name}
        right={
          <>
            {view && view.type !== "refs" && (
              <button type="button" className={`btn${mine ? " on" : ""}`} aria-pressed={mine} onClick={toggleMine} title="Show only your items (Q)">
                <Icon name="filter" />
                Mine <span className="kbd hide-sm">Q</span>
              </button>
            )}
            {member && (
              <button type="button" className="btn pri" onClick={() => setDialog("item")} title="New item (C)">
                <Icon name="plus" />
                <span className="hide-sm">New</span>
              </button>
            )}
            <MenuButton
              label="Project menu"
              className="btn ghost icon"
              align="end"
              items={() => [
                { id: "team", label: producer ? "Team and roles" : "Team", icon: <Icon name="user" />, run: () => setDialog("team") },
                ...(producer ? [{ id: "settings", label: "Project settings", icon: <Icon name="cog" />, run: () => setDialog("settings") }] : []),
                ...(member ? [{ id: "view", label: "New view", icon: <Icon name="plus" />, run: () => setDialog("view") }] : []),
                ...(view && member
                  ? [
                      { id: "done", label: view.config.includeDone ? "Hide done items" : "Show done items", icon: <Icon name="eye" />, run: () => void send("view.upsert", { id: view.id, spaceId: space.id, name: view.name, type: view.type, config: { includeDone: !view.config.includeDone } }) },
                      ...(view.type === "list" || view.type === "table"
                        ? [{ id: "subs", label: view.config.showSubtasks ? "Hide subtasks" : "Show subtasks", icon: <Icon name="layers" />, run: () => void send("view.upsert", { id: view.id, spaceId: space.id, name: view.name, type: view.type, config: { showSubtasks: !view.config.showSubtasks } }) }]
                        : []),
                      ...(producer ? [{ id: "rm", label: `Remove the "${view.name}" view`, danger: true, icon: <Icon name="x" />, run: () => void send("view.remove", { viewId: view.id }).then(() => navigate(spaceHref(space.slug))) }] : []),
                    ]
                  : []),

              ]}
            >
              <Icon name="more" />
            </MenuButton>
          </>
        }
      />
      <div className="vrow">
        <nav className="vtabs" aria-label="Views">
          <Link href={spaceHref(space.slug)} className="vtab" aria-current={!view ? "page" : undefined}>
            <Icon name="home" />
            Overview
          </Link>
          {views.map((v) => (
            <Link key={v.id} href={spaceHref(space.slug, v.id)} className="vtab" aria-current={v.id === view?.id ? "page" : undefined}>
              <Icon name={VIEW_ICON[v.type] ?? "board"} />
              {v.name}
            </Link>
          ))}
          {member && (
            <button type="button" className="vtab" onClick={() => setDialog("view")} aria-label="New view">
              <Icon name="plus" />
            </button>
          )}
        </nav>
      {view && view.type !== "refs" && view.type !== "timeline" && (
        <ViewTools space={space} view={view} kinds={kinds} filters={filters} setFilters={setFilters} overridden={overridden}>
          {(view.type === "board" || view.type === "list") && <GroupBy space={space} view={view} />}
        </ViewTools>
      )}
      </div>
      {view && view.type !== "refs" && view.type !== "timeline" && <FilterChips space={space} view={view} kinds={kinds} filters={filters} setFilters={setFilters} overridden={overridden} />}
      <div className="content" key={view?.id ?? "overview"}>
        <Suspense fallback={null}>
          {view ? <ViewBody space={space} view={view} filters={filters} /> : <Overview space={space} onNewMilestone={() => setDialog("milestone")} onPlan={() => setDialog("plan")} onShip={setShipping} onTeam={() => setDialog("team")} />}
        </Suspense>
      </div>
      {dialog === "item" && <NewItemDialog space={space} kindKey={view ? viewKinds(store.t, space, view)[0]?.key : "task"} parentId={view ? (newItemParent(view) ?? undefined) : undefined} onClose={() => setDialog(null)} />}
      {dialog === "milestone" && <NewItemDialog space={space} kindKey="phase" onClose={() => setDialog(null)} />}
      {dialog === "view" && <NewViewDialog space={space} onClose={() => setDialog(null)} />}
      {dialog === "plan" && <PlanUpdatesDialog space={space} onClose={() => setDialog(null)} />}
      {shipping && <ShipDialog milestone={shipping} onClose={() => setShipping(null)} />}
      {dialog === "settings" && <ProjectSettingsDialog space={space} onClose={() => setDialog(null)} />}
      {dialog === "team" && (producer ? <TeamDialog space={space} onClose={() => setDialog(null)} /> : <TeamList space={space} onClose={() => setDialog(null)} />)}
    </>
  );
}

function ViewBody({ space, view, filters }: { space: Space; view: View; filters: ViewFilters }) {
  switch (view.type) {
    case "board":
      return <Board space={space} view={view} filters={filters} />;
    case "list":
      return <List space={space} view={view} filters={filters} />;
    case "table":
      return <Table space={space} view={view} filters={filters} />;
    case "gallery":
      return <Gallery space={space} view={view} filters={filters} />;
    case "calendar":
      return <Calendar space={space} view={view} filters={filters} />;
    case "timeline":
      return <Timeline space={space} view={view} filters={filters} />;
    case "refs":
      return <Refs space={space} />;
  }
}

function GroupBy({ space, view }: { space: Space; view: View }) {
  const t = useTables();
  const options = groupOptions(viewKinds(t, space, view));
  const current = options.find((o) => o.value === view.config.groupBy) ?? options[0]!;
  return (
    <MenuButton
      label="Group by"
      className="btn sm ghost"
      items={() =>
        options.map((o) => ({
          id: o.value,
          label: o.label,
          checked: o.value === view.config.groupBy,
          run: () => void send("view.upsert", { id: view.id, spaceId: space.id, name: view.name, type: view.type, config: { groupBy: o.value } }),
        }))
      }
    >
      <span className="vt-scope">
        <span className="muted">Group:</span> {current.label}
      </span>
      <Icon name="down" />
    </MenuButton>
  );
}

/** The team, read-only, for members. */
function TeamList({ space, onClose }: { space: Space; onClose: () => void }) {
  const t = useTables();
  const members = [...t.members.values()].filter((m) => m.spaceId === space.id);
  return (
    <Dialog title={`Team on ${space.name}`} onClose={onClose}>
      <div className="subs">
        {members.map((m) => {
          const p = t.actors.get(m.actorId);
          return p ? (
            <div key={m.actorId} className="sub">
              <Avatar actor={p} size="sm" />
              <span className="grow">{p.name}</span>
              <span className="chip">{ROLE_LABEL[m.role]}</span>
            </div>
          ) : null;
        })}
      </div>
    </Dialog>
  );
}
